import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Holder <html lang> + hreflang/canonical-tags i synk med valgt språk og rute.
 * Injiserer per-rute hreflang slik at Google indekserer hver rute som 4 språkversjoner.
 * Bygger absolutte URL-er fra window.location.origin (fungerer både i preview og prod).
 */
const SUPPORTED = ["no", "sv", "da", "en"];
const HTML_LANG_MAP = { no: "nb", sv: "sv", da: "da", en: "en" };

export default function SeoMeta() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Skip alt for link-side-ruter — Cloudflare Worker rewriter stripper
    // hreflang/canonical for allelenker.no. Vi må også sørge for at React
    // ikke re-legger dem inn på klienten når vi er på en /link/*-rute.
    const isLinkRoute = location.pathname.startsWith("/link/");
    if (isLinkRoute) {
      document
        .querySelectorAll('link[data-seo="hreflang"], link[data-seo="canonical"]')
        .forEach((el) => el.remove());
      return;
    }

    const current = (i18n.language || "no").split("-")[0];
    const htmlLang = HTML_LANG_MAP[current] || "nb";
    document.documentElement.setAttribute("lang", htmlLang);

    // Rens tidligere hreflang/canonical-noder som vi selv har lagt til
    document
      .querySelectorAll('link[data-seo="hreflang"], link[data-seo="canonical"]')
      .forEach((el) => el.remove());

    const origin = window.location.origin;
    const path = location.pathname + location.search.replace(/(^|[?&])lang=[^&]+&?/, (m, p1) => (p1 === "?" ? "?" : ""));
    // Fjern hengende ? eller & etter strip
    const cleanPath = path.replace(/[?&]$/, "");

    // canonical = nåværende URL uten ?lang
    const canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    canonical.setAttribute("href", `${origin}${cleanPath}`);
    canonical.setAttribute("data-seo", "canonical");
    document.head.appendChild(canonical);

    // hreflang for hvert språk
    SUPPORTED.forEach((lng) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", lng);
      const sep = cleanPath.includes("?") ? "&" : "?";
      link.setAttribute("href", `${origin}${cleanPath}${sep}lang=${lng}`);
      link.setAttribute("data-seo", "hreflang");
      document.head.appendChild(link);
    });

    // x-default → norsk (primærpublikum)
    const xdefault = document.createElement("link");
    xdefault.setAttribute("rel", "alternate");
    xdefault.setAttribute("hreflang", "x-default");
    xdefault.setAttribute("href", `${origin}${cleanPath}`);
    xdefault.setAttribute("data-seo", "hreflang");
    document.head.appendChild(xdefault);
  }, [location.pathname, location.search, i18n.language]);

  return null;
}
